import {
  deleteCookie,
  getCookie,
  isConsentCategoryEnabled,
  OneTrustCategories,
  setCookie,
} from '@susu/cookie'

import {
  MARKETING_CLICK_ID_COOKIE_FALLBACK,
  MARKETING_CLICK_ID_PARAMS,
  MARKETING_CLICK_IDS_COOKIE_NAME,
  MARKETING_CLICK_IDS_EXPIRY_DAYS,
  type MarketingClickIdParam,
  type MarketingClickIds,
  STORAGE_KEY_MARKETING_CLICK_IDS,
  type StoredMarketingClickIds,
  URL_PARAM_ALIASES,
} from './types'

/**
 * Checks if the value is a valid stored marketing click IDs object.
 */
export function isStoredMarketingClickIds(
  value: unknown,
): value is StoredMarketingClickIds {
  if (value === null || typeof value !== 'object') return false
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.expiresAt === 'number' &&
    candidate.clickIds !== null &&
    typeof candidate.clickIds === 'object'
  )
}

/**
 * Reads a click ID from platform cookies using the fallback map.
 */
export function getClickIdFromCookie(
  param: MarketingClickIdParam,
): string | undefined {
  const cookieName = MARKETING_CLICK_ID_COOKIE_FALLBACK[param]
  if (!cookieName) return undefined
  return getCookie(cookieName) ?? undefined
}

/**
 * Reads marketing click IDs from URL search params (including aliases e.g. ScCid for sccid).
 */
export function getMarketingClickIdsFromUrl(url: URL): MarketingClickIds {
  const result: MarketingClickIds = {}
  for (const param of MARKETING_CLICK_ID_PARAMS) {
    const aliases = URL_PARAM_ALIASES[param] ?? [param]
    for (const name of aliases) {
      const value = url.searchParams.get(name)
      if (value) {
        result[param] = value
        break
      }
    }
  }
  return result
}

const cookieDomain = (): string | undefined => {
  const hostname = window.location.hostname
  return hostname === 'suitsupply.com' || hostname.endsWith('.suitsupply.com')
    ? '.suitsupply.com'
    : undefined
}

const writeClickIdsCookie = (payload: StoredMarketingClickIds): void => {
  try {
    setCookie(MARKETING_CLICK_IDS_COOKIE_NAME, JSON.stringify(payload), {
      expires: new Date(payload.expiresAt),
      path: '/',
      domain: cookieDomain(),
      secure: true,
      sameSite: 'Lax',
    })
  } catch {
    // Cookie write failed — attribution degrades, tracking must not break.
  }
}

const readClickIdsCookie = (): StoredMarketingClickIds | undefined => {
  try {
    const raw = getCookie(MARKETING_CLICK_IDS_COOKIE_NAME)
    if (!raw) return undefined
    const parsed: unknown = JSON.parse(decodeURIComponent(raw))
    if (isStoredMarketingClickIds(parsed) && parsed.expiresAt > Date.now()) {
      return parsed
    }
  } catch {
    // Invalid cookie payload — treat as absent.
  }
  return undefined
}

/**
 * One-time migration of the legacy localStorage entry into the cookie (D17).
 * With C0004 granted the still-valid entry is merged into the cookie; without
 * it (or when expired) the entry is simply removed — marketing data must not
 * be retained without consent. The localStorage key is deleted either way.
 */
export function migrateLegacyClickIdsFromLocalStorage(): void {
  if (typeof window === 'undefined') return
  try {
    // eslint-disable-next-line no-restricted-syntax -- sanctioned: this IS the one-time migration off localStorage
    const raw = window.localStorage.getItem(STORAGE_KEY_MARKETING_CLICK_IDS)
    if (raw === null) return

    if (isConsentCategoryEnabled(OneTrustCategories.ADVERTISING)) {
      const parsed: unknown = JSON.parse(raw)
      if (
        isStoredMarketingClickIds(parsed) &&
        parsed.expiresAt > Date.now() &&
        !readClickIdsCookie()
      ) {
        writeClickIdsCookie(parsed)
      }
    }

    // eslint-disable-next-line no-restricted-syntax -- sanctioned: deleting the legacy entry
    window.localStorage.removeItem(STORAGE_KEY_MARKETING_CLICK_IDS)
  } catch {
    // localStorage unavailable or invalid JSON — nothing to migrate.
  }
}

/**
 * Persists captured marketing click IDs into the `.suitsupply.com` cookie with
 * a FIXED {@link MARKETING_CLICK_IDS_EXPIRY_DAYS}-day expiry from this capture
 * (O6). Only keys present in the capture are overwritten; other stored keys
 * survive. No-op without a captured click ID or without C0004 — the consent
 * gate runs before anything is read or written (O7).
 */
export function persistMarketingClickIds(
  clickIdsFromUrl: MarketingClickIds,
): void {
  if (Object.keys(clickIdsFromUrl).length === 0) return
  if (typeof window === 'undefined') return
  if (!isConsentCategoryEnabled(OneTrustCategories.ADVERTISING)) return

  const existing = readClickIdsCookie()
  writeClickIdsCookie({
    clickIds: { ...existing?.clickIds, ...clickIdsFromUrl },
    expiresAt:
      Date.now() + MARKETING_CLICK_IDS_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
  })
}

const deleteExpiredClickIdsCookie = (): void => {
  // An expired-but-still-serialized payload (clock skew, ITP quirks) is noise —
  // clear it so the next capture starts clean.
  try {
    if (
      getCookie(MARKETING_CLICK_IDS_COOKIE_NAME) !== undefined &&
      readClickIdsCookie() === undefined
    ) {
      deleteCookie(MARKETING_CLICK_IDS_COOKIE_NAME, {
        path: '/',
        domain: cookieDomain(),
      })
    }
  } catch {
    // ignore
  }
}

/**
 * Reads persisted marketing click IDs from the cookie, migrating any legacy
 * localStorage entry first. Returns {} when not in browser, when C0004 is not
 * granted, or when nothing valid is stored.
 */
export function getPersistedMarketingClickIds(): MarketingClickIds {
  if (typeof window === 'undefined') return {}
  if (!isConsentCategoryEnabled(OneTrustCategories.ADVERTISING)) return {}
  migrateLegacyClickIdsFromLocalStorage()
  const stored = readClickIdsCookie()
  if (stored?.clickIds) {
    return stored.clickIds
  }
  deleteExpiredClickIdsCookie()
  return {}
}

/**
 * Reads all marketing click IDs from platform cookies (using cookie fallback map).
 */
export function getMarketingClickIdsFromCookies(): MarketingClickIds {
  const result: MarketingClickIds = {}
  for (const param of MARKETING_CLICK_ID_PARAMS) {
    const value = getClickIdFromCookie(param)
    if (value) {
      result[param] = value
    }
  }
  return result
}
